// Photos attached to an entry, encrypted in the browser before they are uploaded.
//
// The server stores an opaque AES-GCM box and cannot decode it, which has one consequence that
// shapes this whole module: an <img src="…"> pointed at the API would show nothing. Every photo has
// to be fetched as bytes, opened with the DEK, and handed to the DOM as an object URL. That is more
// work than a plain URL, and it is the price of the server not being able to look at them.
//
// Sizing matches iOS (EntryMedia.swift): longest edge 1600px, JPEG quality 0.85. Keeping the two the
// same means a photo looks alike on both, and neither device downloads a picture sized for the other.

import { apiDelete, apiGet, apiGetBytes, apiPatch, apiPutBytes } from './client';
import { openBytes, openWithKey, sealBytes, sealWithKey } from '../crypto/crypto-vault';

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

/**
 * How a photo sits inside its frame.
 *
 * Not a pixel rectangle. Frames differ — the strip is square, the cover is 4:3 — and a rectangle
 * chosen against one of them is wrong in the other. A zoom plus a focal point survives both: the
 * browser fills the frame as it always did, and these say how far in and around what.
 *
 * `x` and `y` are CSS object-position percentages, so 50/50 is exactly the centre crop the browser
 * would have picked unaided, and `scale` of 1 is no zoom. That correspondence is deliberate — an
 * untouched photo and a photo explicitly set back to the middle render identically.
 */
export interface PhotoCrop {
  scale: number;
  x: number;
  y: number;
}

export const DEFAULT_CROP: PhotoCrop = { scale: 1, x: 50, y: 50 };

/** How far in a photo can be pushed before it is being enlarged past what was uploaded. */
export const MAX_CROP_SCALE = 4;

const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/** Bring any crop — including one written by a future client — into a range that renders sanely. */
export function normaliseCrop(crop: PhotoCrop): PhotoCrop {
  return {
    scale: clamp(Number.isFinite(crop.scale) ? crop.scale : 1, 1, MAX_CROP_SCALE),
    x: clamp(Number.isFinite(crop.x) ? crop.x : 50, 0, 100),
    y: clamp(Number.isFinite(crop.y) ? crop.y : 50, 0, 100),
  };
}

export function isDefaultCrop(crop: PhotoCrop | null): boolean {
  if (!crop) return true;
  const c = normaliseCrop(crop);
  return c.scale === 1 && c.x === 50 && c.y === 50;
}

/** Decrypt a stored crop, treating anything unreadable as "no adjustment" rather than failing. */
async function openCrop(dek: CryptoKey, cipher: string | null | undefined): Promise<PhotoCrop | null> {
  if (!cipher) return null;
  try {
    const parsed = JSON.parse(await openWithKey(dek, cipher)) as Partial<PhotoCrop>;
    if (typeof parsed?.scale !== 'number' || typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') {
      return null;
    }
    return normaliseCrop(parsed as PhotoCrop);
  } catch {
    // A crop we cannot open is a cosmetic loss — the photograph is still there, centred. Far better
    // than refusing to show it.
    return null;
  }
}

export interface EntryPhoto {
  id: string;
  entryId: string;
  position: number;
  byteSize: number;
  crop: PhotoCrop | null;
}

interface ServerPhoto {
  id: string;
  entry_id: string;
  position?: number | null;
  byte_size?: number | null;
  crop_cipher?: string | null;
}

async function toPhoto(row: ServerPhoto, dek: CryptoKey): Promise<EntryPhoto> {
  return {
    id: row.id,
    entryId: row.entry_id,
    position: row.position ?? 0,
    byteSize: row.byte_size ?? 0,
    crop: await openCrop(dek, row.crop_cipher),
  };
}

/** The photos on one entry, in display order, with their crops decrypted. */
export async function fetchEntryPhotos(
  dek: CryptoKey,
  entryId: string,
  signal?: AbortSignal,
): Promise<EntryPhoto[]> {
  const res = await apiGet<{ photos?: ServerPhoto[] }>(
    `/api/entries/${encodeURIComponent(entryId)}/photos`,
    signal,
  );
  return Promise.all((res.photos ?? []).map((row) => toPhoto(row, dek)));
}

/**
 * Fetch one photo and decrypt it into an object URL ready for an <img>.
 *
 * The caller owns the returned URL and must revoke it when the image goes away, or the decrypted
 * bytes stay in memory for the life of the tab — which for a page of photographs adds up.
 */
export async function loadEntryPhoto(
  dek: CryptoKey,
  entryId: string,
  photoId: string,
  signal?: AbortSignal,
): Promise<string> {
  const box = await apiGetBytes(
    `/api/entries/${encodeURIComponent(entryId)}/photos/${encodeURIComponent(photoId)}`,
    signal,
  );
  const plain = await openBytes(dek, box);
  // Typed as JPEG because that is what both clients write; the browser sniffs anyway, and a wrong
  // guess here would only affect a hypothetical future format.
  return URL.createObjectURL(new Blob([plain as BlobPart], { type: 'image/jpeg' }));
}

/** Encrypt and upload one already-downscaled photo. */
export async function uploadEntryPhoto(
  dek: CryptoKey,
  entryId: string,
  file: Blob,
  position: number,
  signal?: AbortSignal,
): Promise<EntryPhoto> {
  const jpeg = await downscale(file);
  const sealedBox = await sealBytes(dek, jpeg);

  // Client-chosen rather than server-assigned, so a retry after a dropped upload replaces the object
  // instead of adding a second copy of the same picture.
  //
  // Deliberately no `.jpg`: the id lands in the URL path, and Cloudflare's Hotlink Protection answers
  // any image-looking URL carrying a cross-origin Referer with a 403 HTML page — which the browser
  // then reports only as a failed CORS preflight. The bytes are ciphertext regardless, so an image
  // extension would be a lie that costs a working upload.
  const id = `${Date.now()}_${crypto.randomUUID().slice(0, 8)}`;

  const res = await apiPutBytes<{ id: string; byte_size: number }>(
    `/api/entries/${encodeURIComponent(entryId)}/photos/${encodeURIComponent(id)}?position=${position}`,
    sealedBox,
    signal,
  );

  return { id: res.id, entryId, position, byteSize: res.byte_size ?? sealedBox.byteLength, crop: null };
}

/**
 * Save how a photo sits in its frame, without re-uploading the photo.
 *
 * Sealed like everything else the user wrote: a focal point is a small statement about what is in
 * the picture, and the server is not supposed to be able to read those either. Passing null clears
 * the adjustment and puts the photo back to the frame's own centre.
 */
export async function updateEntryPhotoCrop(
  dek: CryptoKey,
  entryId: string,
  photoId: string,
  crop: PhotoCrop | null,
): Promise<void> {
  // A photo put back where the frame would have placed it anyway has nothing to store, so it clears
  // the column instead of sealing "the centre" — undoing an adjustment leaves no trace of it.
  const wanted = crop ? normaliseCrop(crop) : null;
  const cipher = wanted && !isDefaultCrop(wanted) ? await sealWithKey(dek, JSON.stringify(wanted)) : null;
  await apiPatch(
    `/api/entries/${encodeURIComponent(entryId)}/photos/${encodeURIComponent(photoId)}`,
    { crop_cipher: cipher },
  );
}

export async function deleteEntryPhoto(entryId: string, photoId: string): Promise<void> {
  await apiDelete(
    `/api/entries/${encodeURIComponent(entryId)}/photos/${encodeURIComponent(photoId)}`,
  );
}

/**
 * Re-encode to a JPEG no larger than 1600px on its longest edge.
 *
 * Not an optimisation so much as a requirement: a modern phone photo is 4000px and several megabytes,
 * and uploading that would mean every device that later opens the entry downloads and decrypts all of
 * it to show a thumbnail. The server cannot resize an encrypted image, so whatever is uploaded is
 * exactly what everyone downloads forever.
 */
async function downscale(file: Blob): Promise<Uint8Array> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not prepare the image.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) throw new Error('Could not prepare the image.');
  return new Uint8Array(await blob.arrayBuffer());
}
